import { showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';

import { getInputStatus, hasEmptyValues } from 'helpers';
import { confirmedPasswordValidation, requirePasswordValidation } from 'helpers/validate';
import { apiCall } from 'hooks/service';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';

export const useChangePasswordController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const userId = userProfile.id;

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      password: '',
      confirmedPassword: '',
    },

    validationSchema: Yup.object().shape({
      password: requirePasswordValidation,
      oldPassword: requirePasswordValidation,
      confirmedPassword: confirmedPasswordValidation,
    }),

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const result: { id: string; message: string } = await apiCall({
        url: `/auth/change-password/${userId}`,
        data: {
          ...values,
        },
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        showToastMessage('success', 'Password changed successfully!');
      }
    },
  });

  const { values, errors, isSubmitting, isValid, handleSubmit, handleChange } = formik;

  const { oldPassword, password, confirmedPassword } = values;

  const passwordStatus = getInputStatus(
    password,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.password ? ERROR_MESSAGE.PASSWORD : '',
  );

  const confirmedPasswordStatus = getInputStatus(
    confirmedPassword,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.confirmedPassword
      ? values.confirmedPassword.length >= 6
        ? ERROR_MESSAGE.CONFIRMED_PASSWORD_NOT_MATCHING
        : ERROR_MESSAGE.CONFIRMED_PASSWORD
      : '',
  );

  const oldPasswordStatus = getInputStatus(
    oldPassword,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.oldPassword ? ERROR_MESSAGE.PASSWORD : '',
  );

  const disabled =
    hasEmptyValues({ password, confirmedPassword, oldPassword }) ||
    isSubmitting ||
    !isValid ||
    password !== confirmedPassword;

  return {
    oldPassword,
    password,
    confirmedPassword,
    disabled,
    handleSubmit,
    handleChange,
    passwordStatus,
    confirmedPasswordStatus,
    oldPasswordStatus,
  };
};
